import { Header } from "./components/Header.js";
import { createDataStore } from "./lib/dataStore.js";
import { buildShowPath, parsePath } from "./lib/routes.js";
import { getBlogPostBySlug } from "./lib/blogPosts.js";
import { EXPANDED_SHOWS, getPageMetadata, SITE_URL } from "./lib/seoContent.js";
import { BlogIndexPage } from "./pages/BlogIndexPage.js";
import { BlogPostPage } from "./pages/BlogPostPage.js";
import { CreditsPage } from "./pages/CreditsPage.js";
import { HomePage } from "./pages/Home.js";
import { ShowPage } from "./pages/ShowPage.js";

function projectPrefix() {
  const path = window.location.pathname;
  return path === "/howoldhere" || path.startsWith("/howoldhere/") ? "/howoldhere" : "";
}
function publicPath(path) {
  const prefix = projectPrefix();
  return prefix && (path === prefix || path.startsWith(`${prefix}/`)) ? path : prefix + path;
}
function appPath(path) {
  const prefix = projectPrefix();
  return prefix && (path === prefix || path.startsWith(`${prefix}/`)) ? path.slice(prefix.length) || "/" : path;
}

function setMeta(attribute, name, content) {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  if (content === undefined || content === null) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.append(element);
  }
  element.content = content;
}

// Full document metadata must follow client navigation as well as direct requests.
export function applyPageMetadata(metadata, { noindex = false } = {}) {
  document.title = metadata.title;
  setMeta("name", "description", metadata.description);
  setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");
  setMeta("property", "og:title", metadata.title);
  setMeta("property", "og:description", metadata.description);
  setMeta("property", "og:type", metadata.ogType ?? "website");
  setMeta("property", "og:url", noindex ? null : metadata.canonical);
  const image = metadata.image ?? metadata.imageUrl ?? `${SITE_URL}/og-image.png`;
  setMeta("property", "og:image", image);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", metadata.title);
  setMeta("name", "twitter:description", metadata.description);
  setMeta("name", "twitter:image", image);

  let canonical = document.querySelector('link[rel="canonical"]');
  if (noindex || !metadata.canonical) {
    canonical?.remove();
  } else {
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.append(canonical);
    }
    canonical.href = metadata.canonical;
  }

  for (const stale of document.querySelectorAll('script[type="application/ld+json"], meta[property^="article:"], meta[name="keywords"]')) {
    stale.remove();
  }
  if (!noindex) {
    for (const data of metadata.jsonLd ?? []) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = typeof data === "string" ? data : JSON.stringify(data);
      document.head.append(script);
      const entity = typeof data === "string" ? JSON.parse(data) : data;
      if (entity["@type"] === "BlogPosting") {
        setMeta("property", "article:published_time", entity.datePublished);
        setMeta("property", "article:modified_time", entity.dateModified);
        setMeta("property", "article:section", entity.articleSection);
      }
    }
  }
}

function notFoundPage() {
  const element = document.createElement("section");
  element.className = "page-shell not-found-page";
  element.innerHTML = `
    <div class="not-found-inner">
      <p class="not-found-label">404</p>
      <h1 class="not-found-title">Nothing here.</h1>
      <p class="not-found-desc">This URL didn't match an available page.</p>
      <a class="text-link" data-link href="/">Back to shows</a>
    </div>
  `;
  return { title: "Not Found · HowOldHere", element };
}

export function createRouter({ root, store = createDataStore() }) {
  const shell = document.createElement("div");
  shell.className = "app-shell";
  const headerSlot = document.createElement("div");
  const viewSlot = document.createElement("main");
  viewSlot.className = "view-slot";
  shell.append(headerSlot, viewSlot);
  root.replaceChildren(shell);

  let currentPage = null;
  let currentDepth = 0;
  let currentPath = null;
  const savedScrollByPath = new Map();

  function syncInitialHistoryState() {
    if (typeof history.state?.depth === "number") {
      currentDepth = history.state.depth;
      return;
    }
    history.replaceState({ depth: 0 }, "", `${window.location.pathname}${window.location.search}${window.location.hash}`);
    currentDepth = 0;
  }

  function resolvePage(route, searchParams) {
    if (route.name === "home") return HomePage({ shows: store.getShows(), credits: store.getHeroCredits() });
    if (route.name === "blog") return BlogIndexPage();
    if (route.name === "blog-post" && getBlogPostBySlug(route.params.slug)) return BlogPostPage({ slug: route.params.slug, store });
    if (route.name === "credits") return CreditsPage({ credits: store.getHeroCredits() });
    if (route.name === "show" || route.name === "season") {
      const show = store.getShowById(route.params.id);
      const season = route.name === "season" ? route.params.season : null;
      const seasonAvailable = season === null || (
        EXPANDED_SHOWS.has(show?.id) && show.episodes.some((episode) => episode.season === season)
      );
      if (show && seasonAvailable) {
        return ShowPage({ show, season, episodeId: searchParams.get("episode") });
      }
    }
    return null;
  }

  function trackPageView(page, route) {
    if (typeof window.gtag !== "function") return;
    if (["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)) return;
    window.gtag("event", "page_view", {
      page_title: page.title,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_location: window.location.href,
      route_name: route.name,
    });
  }

  function render(path, { direction = "none", depth = currentDepth } = {}) {
    const url = new URL(path, window.location.origin);
    let route = parsePath(appPath(url.pathname));
    if (route.name === "episode") {
      const match = store.getEpisode(route.params.id, route.params.episodeId);
      if (match) {
        navigate(`${buildShowPath(match.show.id)}?episode=${encodeURIComponent(match.episode.id)}`, { replace: true });
        return;
      }
      route = { name: "not-found", params: {} };
    }

    if (currentPath) savedScrollByPath.set(currentPath, window.scrollY);
    let page = resolvePage(route, url.searchParams);
    if (!page) {
      route = { name: "not-found", params: {} };
      page = notFoundPage();
    }
    const show = route.params.id ? store.getShowById(route.params.id) : null;
    const post = route.name === "blog-post" ? getBlogPostBySlug(route.params.slug) : null;
    const metadata = getPageMetadata(route, show, post);
    page.title = metadata.title;
    applyPageMetadata(metadata, { noindex: route.name === "not-found" || metadata.noindex === true });

    currentPage?.destroy?.();
    currentPage = page;
    currentPath = `${url.pathname}${url.search}`;
    currentDepth = depth;
    headerSlot.replaceChildren(Header({ route, store }));
    page.element.classList.add("page", "is-visible");
    page.element.dataset.direction = direction;
    viewSlot.replaceChildren(page.element);

    for (const link of shell.querySelectorAll('a[href^="/"]')) {
      const href = link.getAttribute("href");
      if (!href.startsWith("//")) link.setAttribute("href", publicPath(href));
    }
    const savedScroll = direction === "back" ? (savedScrollByPath.get(currentPath) ?? 0) : 0;
    viewSlot.scrollTop = 0;
    window.scrollTo(0, savedScroll);
    trackPageView(page, route);
  }

  function navigate(path, { replace = false } = {}) {
    const target = new URL(publicPath(path), window.location.origin);
    const destination = `${target.pathname}${target.search}${target.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!replace && destination === current) return;
    const nextDepth = replace ? currentDepth : currentDepth + 1;
    history[replace ? "replaceState" : "pushState"]({ depth: nextDepth }, "", destination);
    render(destination, { direction: replace ? "none" : "forward", depth: nextDepth });
  }

  function handleLinkClick(event) {
    const link = event.target.closest("a[data-link]");
    if (!link || event.defaultPrevented || event.button !== 0 || link.hasAttribute("download")) return;
    if (link.target === "_blank" || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
    event.preventDefault();
    navigate(`${url.pathname}${url.search}${url.hash}`);
  }

  function handlePopState(event) {
    const nextDepth = typeof event.state?.depth === "number" ? event.state.depth : 0;
    render(`${window.location.pathname}${window.location.search}${window.location.hash}`, {
      direction: nextDepth < currentDepth ? "back" : "forward", depth: nextDepth,
    });
  }

  return {
    start() {
      syncInitialHistoryState();
      document.addEventListener("click", handleLinkClick);
      window.addEventListener("popstate", handlePopState);
      render(`${window.location.pathname}${window.location.search}${window.location.hash}`, { depth: currentDepth });
    },
    navigate,
  };
}
